import React, { CSSProperties } from 'react';

import { GestureEvent } from '../../../../../Events/withEvent';
import { Toast } from '../../../../../Web/Services/ToastService';
import { useGestureContext } from '../../../Context/GestureContext';
import { useMapContext, useMapContextState } from '../../../Context/MapContext';
import { MapConstants } from '../../../MapConstants';
import { IRoom, RoomType } from '../../../Models/IRoom';
import { IRoomItem } from '../../../Models/IRoomItem';
import { InvalidPositionDetection } from '../../../Utils/Collisions/InvalidPositionDetection';
import { RoomMove } from '../../../Utils/Room/RoomMove';
import { CoordPoint, Wall } from '../../../Utils/Types';
import { WallResizeDirection, WallUtils } from '../../../Utils/Wall/WallUtils';
import { qSVG } from '../../../Utils/qSVG';
import {
    CoteConstants,
    CoteEndArrow,
    CoteLabel,
    CoteLine,
    CoteLineEnd,
    CoteLock,
    CoteSizeText,
    CoteUtils,
} from '../Cotes';
import { EditCoteDialog } from './EditCoteDialog';
import { useUndoRedoMap } from '../../../Context/UndoRedoMapContext';
import { useScaleCoteSizeText, useTextSizeRectWidth } from './ZoomScaleComponents';
import { Editor } from '../../../Utils/Editor';
import { getMapByType, toUpdatedMapState } from 'Plugins/FloorPlan/Utils/Room/MapByType';
import { MapHelper } from 'Plugins/FloorPlan/Helpers/MapHelper';

type BoxRibsProps = {
    readonly?: boolean;
    roomId?: string;
    walls?: Array<Wall>;
    rooms?: Array<IRoom>;
    roomItems?: Array<IRoomItem>;
    selectedRoomId?: string;
    selectedRoomItemId?: string;
    selectedOpeningId?: string;
    type?: RoomType;
    shrinkScale?: number;
};
export const MapBoxRoomsRibs = ({ readonly, roomId }: BoxRibsProps) => {
    const {
        rooms = [],
        roomsIdsByType,
        selectedRoomId,
        selectedRoomItemId,
        selectedOpeningId,
    } = getMapByType(useMapContextState());

    const roomsToUse = rooms.filter((item) => roomsIdsByType.includes(item.roomId!) || !item.roomId);
    return (
        <RawBoxRoomsRibs
            walls={roomsToUse.flatMap((room) => room.walls || [])}
            rooms={roomsToUse}
            roomItems={roomsToUse.flatMap((room) => room.roomItems || [])}
            selectedRoomId={selectedRoomId}
            selectedRoomItemId={selectedRoomItemId}
            selectedOpeningId={selectedOpeningId}
            readonly={readonly}
            roomId={roomId}
        />
    );
};

export const RawBoxRoomsRibs = ({
    readonly,
    roomId,
    walls: ribWalls = [],
    rooms = [],
    roomItems = [],
    selectedRoomId,
    selectedRoomItemId,
    selectedOpeningId,
    type,
    shrinkScale,
}: BoxRibsProps) => {
    const { rooms: allRooms = [] } = useMapContextState();
    const allWalls = MapHelper.getWalls(allRooms);
    const allRoomItems = MapHelper.getRoomItems(allRooms);
    let roomsToDisplay = rooms;
    let roomItemsToDisplay = roomItems;
    let wallsToDisplay = ribWalls;
    if (type) {
        const roomIds = rooms.filter((room) => room.type === type).map((room) => room.roomId!);
        roomsToDisplay = allRooms.filter((item) => roomIds.includes(item.roomId!) || !item.roomId);
        wallsToDisplay = allWalls.filter((item) => roomIds.includes(item.roomId!) || !item.roomId);
        roomItemsToDisplay = allRoomItems.filter((item) => roomIds.includes(item.roomId!) || !item.roomId);
    }
    const gesture = useGestureContext().state;
    const ribWallsToUse = wallsToDisplay.filter((wall) => wall.roomId === roomId || !roomId);
    const adjacentWalls = WallUtils.getWallsAdjacentList(ribWallsToUse, ribWallsToUse, roomItemsToDisplay);

    const showCotes = (room: IRoom) =>
        readonly ||
        (selectedRoomItemId || selectedOpeningId || gesture.initialRoomItem?.id || gesture.openingId
            ? false
            : selectedRoomId
            ? room.roomId === selectedRoomId
            : true);
    return (
        <React.Fragment>
            {roomsToDisplay
                .filter((item) => (roomId ? item.roomId === roomId : true))
                .filter(showCotes)
                .map((room) => {
                    return (
                        <BoxWallCotes
                            key={`BoxWallCotes-Room-${room.roomId}`}
                            roomId={room.roomId!}
                            ribWalls={room.walls!}
                            adjacentWalls={adjacentWalls}
                            shrinkScale={shrinkScale}
                            readonly={readonly}
                        />
                    );
                })}
        </React.Fragment>
    );
};

type BoxWallRibsProps = {
    roomId: string;
    ribWalls: Array<Wall>;
    adjacentWalls: Array<Array<Wall>>;
    withAdjacentCotes?: boolean;
    shrinkScale?: number;
    readonly?: boolean;
};
const BoxWallCotes = ({ roomId, ribWalls, adjacentWalls, shrinkScale, readonly }: BoxWallRibsProps) => {
    const insideCoteOnly = readonly;
    const wallSize = (wall: Wall) => qSVG.measure(wall.start, wall.end);
    const map = useMapContext();
    const undoRedo = useUndoRedoMap();
    const [foregroundCote, setForegroundCote] = React.useState<(Wall & { ogIndex: number }) | undefined>(undefined);
    let coteWallToEdit: Wall | undefined = undefined;
    if (!insideCoteOnly) {
        const { coteWallToEdit: cwte = undefined } = map.getState();
        coteWallToEdit = cwte;
    }

    const { lockedWallsList = [] } = map.getState();

    //** Simple auto direction. Tmp */
    const onValidate = (notLockedWallDirections: Array<WallResizeDirection>, dimens: number) => {
        const mapState = map.getState();
        const { rooms = [] } = getMapByType(mapState);

        if (coteWallToEdit) {
            const walls = rooms.flatMap((room) => room.walls || []);
            //lister les directions qui ne sont pas lockées

            let updatedStates:
                | {
                      newAllWalls: Wall[];
                      newAllRooms: IRoom[];
                  }
                | undefined = undefined;
            let invalidRoomIds: Array<string> = [];
            let i = 0;
            do {
                const direction = notLockedWallDirections[i];
                updatedStates = WallUtils.resizeWall(walls, rooms, coteWallToEdit, dimens, direction);
                invalidRoomIds = InvalidPositionDetection.getInvalidRoomIds(updatedStates.newAllRooms, roomId);
                i++;
            } while (i < notLockedWallDirections.length && invalidRoomIds.length > 0);

            if (invalidRoomIds.length > 0) {
                Toast.error('ERROR_INVALID_WALL_MOVE');
                map.update({
                    values: {
                        busy: false,
                    },
                    logs: { event: 'BoxWallCotes onValidate error', dimens },
                });
            } else {
                undoRedo.store(mapState);
                Editor.architect(updatedStates.newAllWalls, updatedStates.newAllRooms);
                const updateMapValues = { walls: updatedStates.newAllWalls, rooms: updatedStates.newAllRooms };
                map.update({
                    values: { ...toUpdatedMapState(mapState, updateMapValues), busy: false },
                    mapModified: true,
                    logs: {
                        event: 'BoxWallCotes onValidate success',
                        dimens,
                    },
                });
            }
            map.update({ values: { coteWallToEdit: undefined } });
        }
    };

    const getWallLimits = (coteWallToEdit: Wall, direction: WallResizeDirection) => {
        const { rooms = [] } = map.getState();
        const walls = MapHelper.getWalls(rooms);
        return getMinMaxResizeWall(coteWallToEdit, walls, direction);
    };

    const props: React.SVGProps<SVGGElement> = {
        id: RoomMove.roomRibsId(roomId),
        className: RoomMove.class(roomId),
    };

    const wallsWithOgIndex: Array<Wall & { ogIndex: number }> = ribWalls.map((wall, index) => ({
        ...wall,
        ogIndex: index,
    }));
    const ribWallsOrdered = foregroundCote
        ? [...wallsWithOgIndex.filter((x) => x.wallId !== foregroundCote?.wallId), foregroundCote]
        : wallsWithOgIndex;

    return (
        <React.Fragment>
            <g {...props}>
                {ribWallsOrdered.map((wall) => {
                    const adjWall = adjacentWalls.find((x) => x[0] === wall);
                    return (
                        <WallRibCote
                            metaId="wall¤wallStart¤wallEnd" //metaId
                            attrs={props}
                            key={wall.ogIndex}
                            roomId={wall.roomId!}
                            p1={wall.start}
                            p2={wall.end}
                            inOut={adjWall || insideCoteOnly ? 'inside' : 'outside'}
                            side={wall.sideName!}
                            value={wallSize(wall)}
                            angleDeg={wall.angleRad * (180 / Math.PI)}
                            coteMeasureOffset={insideCoteOnly ? MapConstants.wallThickness : 0}
                            highlight={
                                coteWallToEdit
                                    ? wall.roomId === coteWallToEdit.roomId && wall.sideName === coteWallToEdit.sideName
                                    : false
                            }
                            locked={lockedWallsList.some(
                                (x) => x.roomId === wall.roomId && x.wallIndex === wall.ogIndex
                            )}
                            editCote={() => {
                                const { lockedWallsList = [] } = map.getState();
                                const newLockedWallsList = lockedWallsList.filter(
                                    (x) => !(x.roomId === wall.roomId && x.wallIndex === wall.ogIndex)
                                );
                                map.update({
                                    values: {
                                        lockedWallsList: newLockedWallsList,
                                        coteWallToEdit: wall,
                                    },
                                });
                            }}
                            lockSize={() => {
                                const { lockedWallsList = [] } = map.getState();
                                const isAlreadyLocked = lockedWallsList.some(
                                    (x) => x.roomId === wall.roomId && x.wallIndex === wall.ogIndex
                                );
                                if (isAlreadyLocked) {
                                    const newLockedWallsList = lockedWallsList.filter(
                                        (x) => x.roomId !== wall.roomId || x.wallIndex !== wall.ogIndex
                                    );
                                    map.update({ values: { lockedWallsList: newLockedWallsList } });
                                    return;
                                } else {
                                    const newLockedWallsList = [
                                        ...lockedWallsList,
                                        { roomId: wall.roomId!, wallIndex: wall.ogIndex },
                                    ];
                                    map.update({ values: { lockedWallsList: newLockedWallsList } });
                                }
                            }}
                            shrinkScale={shrinkScale}
                            readonly={readonly}
                            onMouseOver={() => setForegroundCote(wall)}
                            onMouseOut={() => setForegroundCote(undefined)}
                        />
                    );
                })}
            </g>
            {coteWallToEdit && coteWallToEdit.roomId === roomId && (
                <EditCoteDialog
                    value={wallSize(coteWallToEdit).toFixed(0)}
                    onValidate={onValidate}
                    onClose={() => map.update({ values: { coteWallToEdit: undefined } })}
                />
            )}
        </React.Fragment>
    );
};

type WallRibCoteProps = {
    roomId: string;
    p1: CoordPoint; //start
    p2: CoordPoint; //end
    inOut: 'inside' | 'outside';
    side: string;
    value: number;
    angleDeg: number;
    metaId: string;
    coteMeasureOffset: number;
    attrs: React.SVGProps<SVGGElement>;

    highlight?: boolean;
    locked: boolean;
    editCote: (e?: GestureEvent) => void;
    lockSize: (e?: GestureEvent) => void;
    shrinkScale?: number;
    readonly?: boolean;
    onMouseOver?: (e: React.MouseEvent<SVGGElement, MouseEvent>) => void;
    onMouseOut?: (e: React.MouseEvent<SVGGElement, MouseEvent>) => void;
};
const WallRibCote = ({
    roomId,
    p1,
    p2,
    inOut,
    side,
    value,
    angleDeg: angle,
    metaId,
    coteMeasureOffset = 0,
    highlight,
    editCote,
    locked,
    lockSize,
    shrinkScale,
    readonly,
    onMouseOver,
    onMouseOut,
}: WallRibCoteProps) => {
    const startText = qSVG.middle(p1.x, p1.y, p2.x, p2.y);
    const measure = qSVG.measure(p1, p2) - coteMeasureOffset;
    const densedDisplay = value < CoteConstants.densedDisplayRuptureValue(shrinkScale);

    const sizeText = value.toFixed(0) + ' ' + CoteConstants.quote_unit;
    const sizeTextLength = useTextSizeRectWidth(sizeText.length);
    const fontSize = useScaleCoteSizeText();

    const { angle: newAngle, shiftValue, reversed } = CoteUtils.calculateShift(inOut, angle, shrinkScale);
    const densedShiftOffset = CoteUtils.calculateDensedShiftOffset(reversed, densedDisplay, shrinkScale);

    const props: React.SVGProps<SVGGElement> = {
        id: `wall:room-${roomId}¤side-${side}`,
        className: 'cote',
        transform: `rotate(${newAngle} ${startText.x},${startText.y}) translate(${startText.x},${
            startText.y + (shiftValue + densedShiftOffset)
        })`,
    };

    const labelStyle: CSSProperties = {
        cursor: 'pointer',
        transform: newAngle === -270 || newAngle === 90 ? 'rotate(180deg)' : 'none',
    };

    return (
        <g {...props} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
            <CoteLine measure={measure} />
            <CoteLineEnd
                x={-measure / 2}
                shiftValue={shiftValue + densedShiftOffset}
                reversed={reversed}
                inOut={inOut}
                shrinkScale={shrinkScale}
            />
            <CoteLineEnd
                x={measure / 2}
                shiftValue={shiftValue + densedShiftOffset}
                reversed={reversed}
                inOut={inOut}
                shrinkScale={shrinkScale}
            />
            <CoteEndArrow x={-measure / 2} reversed shrinkScale={shrinkScale} />
            <CoteEndArrow x={measure / 2} shrinkScale={shrinkScale} />

            <CoteLabel
                textLength={sizeTextLength}
                additionalWidth={readonly ? 0 : 16}
                highlight={highlight}
                shrinkScale={readonly ? 1 : shrinkScale}
            />
            <g style={labelStyle}>
                <CoteSizeText
                    text={sizeText}
                    locked={locked}
                    fontSize={fontSize}
                    onClick={editCote}
                    x={readonly ? 0 : -8}
                    shrinkScale={shrinkScale}
                    readonly={readonly}
                />
                {readonly ? null : <CoteLock textLength={sizeText.length} locked={locked} onClick={lockSize} shrinkScale={shrinkScale} />}
            </g>
        </g>
    );
};

const getMinMaxResizeWall = (testedWall: Wall, walls: Array<Wall>, direction: WallResizeDirection) => {
    const wallMinSizeCm = testedWall.minimumSize!;
    const minSizeLimit = MapConstants.wallMinSize();
    const min = wallMinSizeCm >= minSizeLimit ? wallMinSizeCm : minSizeLimit;
    let max = MapConstants.wallMaxSize;

    // disabled. TODO handle compass resize related to #50321

    // let intersectionDistances: number[] = [];
    // for (const wall of walls) {
    //     let intersection = qSVG.intersectionOfEquations(testedWall.equations.base, wall.equations.base, 'obj') as
    //         | CoordPoint
    //         | undefined;
    //     if (
    //         intersection &&
    //         intersection.x &&
    //         intersection.y &&
    //         !PointUtils.arePointsNearlyEquals(intersection, testedWall.start) &&
    //         !PointUtils.arePointsNearlyEquals(intersection, testedWall.end) &&
    //         Editor.testPointBelongToWall(wall, intersection)
    //     ) {
    //         if (direction === WallResizeDirection.Both || direction === WallResizeDirection.FixedEnd) {
    //             if (!Editor.testPointBelongToLine([testedWall.start, intersection], testedWall.end)) {
    //                 intersectionDistances.push(qSVG.measure(testedWall.start, intersection));
    //             }
    //         }
    //         if (direction === WallResizeDirection.Both || direction === WallResizeDirection.FixedStart) {
    //             if (!Editor.testPointBelongToLine([testedWall.end, intersection], testedWall.start)) {
    //                 intersectionDistances.push(qSVG.measure(testedWall.end, intersection));
    //             }
    //         }
    //     }
    // }

    // if (intersectionDistances.length > 0) {
    //     if (direction === WallResizeDirection.Both) {
    //         max = (Math.min(...intersectionDistances) + WallUtils.wallSize(testedWall) / 2) * 2;
    //     } else if (direction === WallResizeDirection.FixedStart) {
    //         max = Math.min(...intersectionDistances) + WallUtils.wallSize(testedWall);
    //     } else if (direction === WallResizeDirection.FixedEnd) {
    //         max = Math.min(...intersectionDistances) + WallUtils.wallSize(testedWall);
    //     }
    // }

    return { min: Math.ceil(min), max: Math.floor(max) };
};
